import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import Head from 'next/head';

// utils
import getServerSideTranslations from '../../src/utils/server-side';
import getServerDefaultLayout from '../../src/utils/getServerDefaultLayout';

// Services
import { getContentTranslationProxy } from '../../src/services/content-translations';

// Components
import EventsLayout from '../../components/templates/EventsLayout/EventsLayout';

// Hooks
import useSeoLinks from '../../src/hooks/useSeoLinks';

const queryKey = ['page', 'events'];

const PAGE_SIZE = 99;

function Index({
  header, footer, categories, events,
}) {
  // Hooks
  const { locale } = useRouter();
  const { canonicalUrl } = useSeoLinks();
  const [params, setParams] = useState({ category: undefined, page: 1 });

  // Handle initial category from URL hash
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const categoryFromHash = categories.find(
          (categoryData) => categoryData?.data?.tag.toLowerCase() === hash.toLowerCase(),
        );

        if (categoryFromHash) {
          setParams({
            category: {
              name: categoryFromHash.data.tag,
              id: categoryFromHash.contentId,
            },
            page: 1,
          });
        }
      }
    }
  }, [categories]);

  // Update URL hash when category changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (params.category?.name && params.category.name !== undefined) {
        window.location.hash = params.category.name.toLowerCase();
      } else if (params.category?.name === undefined) {
        window.location.hash = '';
      }
    }
  }, [params.category]);

  // Data
  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [...queryKey, params?.category?.id, params?.page || 1],
    queryFn: ({ pageParam = 1 }) => getContentTranslationProxy({
      contentTypeId: 'event',
      pageNumber: pageParam,
      pageSize: PAGE_SIZE,
      referencingContentId: params?.category?.id,
    }, locale),
    initialData: events,
    keepPreviousData: true,
    getNextPageParam: (lastPage) => lastPage?.pagination?.nextPage,
    getPreviousPageParam: (firstPage) => firstPage?.pagination?.previousPage,
  });

  const eventsList = data.pages?.reduce((acc, page) => [...acc, ...page.data], []);
  const hasNextPage = data?.pages
    ? data?.pages[data.pages.length - 1].pagination.nextPage !== null
    : false;

  const loadMore = () => {
    fetchNextPage();
  };

  const handleCategoryClick = (selectedCategory) => {
    if (selectedCategory === 'all') {
      return setParams(
        {
          category: undefined,
          page: 1,
        },
      );
    }

    const selectedCategoryObject = categories.find(
      (categoryData) => categoryData?.data?.tag === selectedCategory,
    );
    return setParams(
      {
        category: { name: selectedCategory, id: selectedCategoryObject?.contentId },
        page: 1,
      },
    );
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="noindex" />
      </Head>
      <EventsLayout
        header={header?.[0]?.data}
        footer={footer?.[0]?.data}
        categories={categories}
        events={eventsList}
        onCategoryClick={handleCategoryClick}
        selectedCategory={params?.category}
        hasNextPage={hasNextPage}
        isLoadingMore={isFetching}
        onLoadMore={loadMore}
      />
    </>
  );
}

Index.propTypes = {
  header: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
  footer: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        category: PropTypes.string.isRequired,
      }),
      contentId: PropTypes.string,
    }),
  ).isRequired,
  events: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.shape({}),
    })),
  }).isRequired,
};

export async function getServerSideProps({ locale, query }) {
  const translations = await getServerSideTranslations(locale);
  const preview = { preview: query.previewMode ? true : null, contentId: query.contentId || null };

  const queryClient = new QueryClient();
  const eventContentTypeId = 'event';

  const { header, footer } = await getServerDefaultLayout(queryClient, locale, null, 'contact');

  const events = await queryClient.fetchQuery({
    queryKey: [...queryKey, null],
    queryFn: () => getContentTranslationProxy({
      contentTypeId: eventContentTypeId,
      pageNumber: query.page || 1,
      pageSize: PAGE_SIZE,
    }, locale),
  });

  const categories = await queryClient.fetchQuery(
    ['page', { contentTypeId: 'tag', locale }],
    () => getContentTranslationProxy({ contentTypeId: 'tag', ...preview }, locale, query.previewMode)
      .then(async (res) => {
        if ((res?.data?.length)) {
          return res?.data;
        }
        return [];
      }),
  );

  return {
    props: {
      header,
      footer,
      categories,
      events,
      ...translations,
    },
  };
}
export default Index;
